export const IMPORT_FIELDS = [
  'name',
  'launchDate',
  'launchTime',
  'notificationTitle',
  'notificationBody',
  'notificationLink',
  'utmCampaign',
  'labels',
] as const;
export type ImportField = (typeof IMPORT_FIELDS)[number];

/** `launchDate` may hold the date alone or date and time together; `launchTime` is optional. */
export const REQUIRED_IMPORT_FIELDS = [
  'name',
  'launchDate',
  'notificationTitle',
  'notificationBody',
] as const satisfies readonly ImportField[];

export type MappingConfidence = 'high' | 'medium';

export interface FieldSuggestion {
  field: ImportField;
  /** Index of the CSV column. */
  column: number;
  confidence: MappingConfidence;
}

/** Normalized header (no accents, no case, no punctuation) -> field, in pt and en. */
const SYNONYMS: Record<ImportField, string[]> = {
  name: [
    'nome',
    'nome da campanha',
    'nome campanha',
    'campanha',
    'name',
    'campaign name',
    'campaign',
  ],
  launchDate: [
    'data',
    'data de envio',
    'data de disparo',
    'data e hora',
    'data hora',
    'quando',
    'envio',
    'agendamento',
    'date',
    'send date',
    'launch date',
    'launch date time',
    'date time',
    'datetime',
    'when',
  ],
  launchTime: [
    'hora',
    'horario',
    'hora de envio',
    'hora de disparo',
    'time',
    'send time',
    'launch time',
  ],
  notificationTitle: [
    'titulo',
    'assunto',
    'titulo da notificacao',
    'titulo do push',
    'title',
    'subject',
    'notification title',
  ],
  notificationBody: [
    'mensagem',
    'texto',
    'corpo',
    'corpo da notificacao',
    'conteudo',
    'body',
    'message',
    'text',
    'notification body',
  ],
  notificationLink: [
    'link',
    'url',
    'link da notificacao',
    'destino',
    'notification link',
    'deep link',
  ],
  utmCampaign: [
    'utm',
    'utm campaign',
    'utmcampaign',
    'utm campanha',
    'campanha utm',
  ],
  labels: ['etiquetas', 'etiqueta', 'labels', 'label', 'tags', 'tag'],
};

const EXACT_SCORE = 100;
const PARTIAL_SCORE = 60;
const CONTENT_SCORE = 10;
/** Share of the non-empty values that must look like the type for the content signal. */
const CONTENT_MATCH_RATIO = 0.8;

const DATE_LIKE =
  /^(\d{1,2}[/.-]\d{1,2}[/.-]\d{4}|\d{4}-\d{2}-\d{2})([ T]\d{1,2}:\d{2}(:\d{2}(\.\d+)?)?\s*(Z|[+-]\d{2}(:?\d{2})?)?)?$/i;
const TIME_LIKE = /^\d{1,2}:\d{2}(:\d{2})?$/;

function normalizeHeader(header: string): string {
  return header
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function isUrl(value: string): boolean {
  if (!/^https?:\/\//i.test(value)) return false;
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

const CONTENT_MATCHERS: Partial<
  Record<ImportField, (value: string) => boolean>
> = {
  notificationLink: isUrl,
  launchDate: (value) => DATE_LIKE.test(value),
  launchTime: (value) => TIME_LIKE.test(value),
};

function nameScore(normalizedHeader: string, field: ImportField): number {
  if (SYNONYMS[field].includes(normalizedHeader)) return EXACT_SCORE;

  const padded = ` ${normalizedHeader} `;
  const containsSynonym = SYNONYMS[field].some((synonym) =>
    padded.includes(` ${synonym} `)
  );
  return containsSynonym ? PARTIAL_SCORE : 0;
}

function columnLooksLike(
  column: number,
  field: ImportField,
  sampleRows: string[][]
): boolean {
  const matcher = CONTENT_MATCHERS[field];
  if (!matcher) return false;

  const values = sampleRows
    .map((row) => (row[column] ?? '').trim())
    .filter((value) => value !== '');
  if (values.length === 0) return false;

  const matching = values.filter(matcher).length;
  return matching / values.length >= CONTENT_MATCH_RATIO;
}

/**
 * Suggests one column per field, from the header name (exact synonym, then header that
 * contains a synonym) with the content of the sample rows as tie-breaker and last resort.
 * A column that exactly matches one field is not offered to the others by partial or content
 * signals. The same column may still serve two fields when the user maps it by hand.
 */
export function suggestMapping(
  headers: string[],
  sampleRows: string[][]
): FieldSuggestion[] {
  const normalized = headers.map(normalizeHeader);

  const exactOwners = normalized.map((header) =>
    IMPORT_FIELDS.filter((field) => nameScore(header, field) === EXACT_SCORE)
  );

  const suggestions: FieldSuggestion[] = [];
  for (const field of IMPORT_FIELDS) {
    const candidates = normalized
      .map((header, column) => {
        const byName = nameScore(header, field);
        const ownedByOtherField =
          exactOwners[column].length > 0 && byName !== EXACT_SCORE;
        if (ownedByOtherField) return { column, name: 0, score: 0 };

        const byContent = columnLooksLike(column, field, sampleRows)
          ? CONTENT_SCORE
          : 0;
        return { column, name: byName, score: byName + byContent };
      })
      .filter((candidate) => candidate.score > 0);
    if (candidates.length === 0) continue;

    const best = candidates.reduce((top, candidate) =>
      candidate.score > top.score ? candidate : top
    );
    const tiedOnName = candidates.filter(
      (candidate) => candidate.name === best.name && candidate.name > 0
    );
    const isClear = best.name === EXACT_SCORE && tiedOnName.length === 1;

    suggestions.push({
      field,
      column: best.column,
      confidence: isClear ? 'high' : 'medium',
    });
  }

  return suggestions;
}
